import React, { useState, useEffect } from 'react';
import moment from 'moment-timezone';
import { QueryClient, QueryClientProvider, useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import I18n from '@/modules/i18n';
import { plato_api_subscriber_terms_path, plato_api_subscriber_path } from '@/modules/routes';
import Modal, { Footer } from '@/components/common/Modal';

interface AcademicTermsModalProps {
  modalManager: ModalManager;
  subscriberId: string;
}

interface TermType {
  id: string,
  name: string,
  start_date: string,
  end_date: string,
}

interface SubscriberTermSettings {
  term_ended_in_days: number | null,
  term_start_in_days: number | null,
}

// AIDEV-NOTE: these days are a subscriber-wide setting (PLAT-17853). They used to be written to every
// `terms` row, which silently no-op'd whenever the subscriber had no term in the current academic session.
const AcademicTermsModal = ({ modalManager, subscriberId }: AcademicTermsModalProps) => {
  const [startGracePeriod, setStartGracePeriod] = useState('0');
  const [endGracePeriod, setEndGracePeriod] = useState('0');
  const [termsWithRange, setTermsWithRange] = useState({});

  const inputClasses = 'tw-w-5 tw-pl-[4px] tw-pr-[15px] tw-py-3 tw-m-2 tw-rounded-md tw-border '
    + 'tw-border-gray-400 tw-border-solid tw-text-right';

  const { data: terms, isLoading } = useQuery({
    queryFn: () => (
      axios
        .get(plato_api_subscriber_terms_path(subscriberId))
        .then(res => res.data.data)
    ),
    queryKey: ['terms', subscriberId],
  });

  const { data: settings } = useQuery<SubscriberTermSettings>({
    // #show is root-wrapped as { subscriber: ... }, unlike the { data: ... } that #index returns.
    queryFn: () => (
      axios
        .get(plato_api_subscriber_path(subscriberId))
        .then(res => res.data.subscriber)
    ),
    queryKey: ['subscriber', subscriberId],
  });

  const { isError, isLoading: isSaving, mutate: saveSettings } = useMutation({
    mutationFn: () => axios.patch(plato_api_subscriber_path(subscriberId), {
      subscriber: {
        term_ended_in_days: endGracePeriod,
        term_start_in_days: startGracePeriod,
      },
    }),
    onSuccess: () => { modalManager.close(); },
  });

  // Take the first 10 characters of the datetime to consider it just a date.
  const momentWithIgnoredTime = date => moment((date || '').slice(0, 10));

  const checkTerms = () => {
    const calculatedTermsHash = {};

    // check if the current date is between the term's start and end dates -/+ the grace period
    terms.forEach((term: TermType) => {
      const isWithinRange = (t: TermType) => {
        // we subtract from start date and add to end date to extend the ranges that they include
        // even if it seems like we should be doing the opposite
        const startDateWithGrace = momentWithIgnoredTime(t.start_date)
          .subtract(startGracePeriod, 'days')
          .format('YYYY-MM-DD');
        const endDateWithGrace = momentWithIgnoredTime(t.end_date)
          .add(endGracePeriod, 'days')
          .format('YYYY-MM-DD');

        // Use UTC date, since that's what the backend sync will use.
        const today = moment.utc().format('YYYY-MM-DD');

        // Can use lexicographical date comparison since all are formatted YYYY-MM-DD
        const startDateWithinRange = t.start_date == null || startDateWithGrace <= today;
        const endDateWithinRange = t.end_date == null || today <= endDateWithGrace;

        return startDateWithinRange && endDateWithinRange;
      };

      // store whether or not the term is within range in a hash
      calculatedTermsHash[term.id] = isWithinRange(term);
    });

    setTermsWithRange(calculatedTermsHash);
  };

  // Initial load to set the start and end grace input values.
  useEffect(() => {
    if (!settings) return;

    setStartGracePeriod(String(settings.term_start_in_days ?? 0));
    setEndGracePeriod(String(settings.term_ended_in_days ?? 0));
  }, [settings]);

  // No isLoading guard: terms is only truthy once the query has resolved with data.
  useEffect(() => {
    if (!terms) return;

    checkTerms();
  }, [terms, startGracePeriod, endGracePeriod]);

  const termsAndRanges = () => {
    const formattedTerm = (term: TermType) => {
      const inRangeIcon = termsWithRange[term.id] ? 'fa fa-check text--green' : 'fa fa-times tw-text-red';

      const formattedDate = (date) => {
        if (date == null) return 'N/A';

        return momentWithIgnoredTime(date).format('MM/DD/YYYY');
      };

      return (
        <div key={term.id}>
          <i aria-hidden className={`${inRangeIcon} tw-mr-2 tw-w-5`} />
          <span>{term.name} ({formattedDate(term.start_date)} - {formattedDate(term.end_date)})</span>
        </div>
      );
    };

    if (terms.length === 0) return <p>{I18n.t('no_academic_terms_this_year')}</p>;

    return terms.map(term => formattedTerm(term));
  };

  return (
    <Modal closeModal={modalManager.close} headerText="Select Academic Terms" isOpen={modalManager.isOpen}>
      <div className="tw-text-lg">
        <div>
          <p>Include <strong>all active academic terms</strong> and any that:</p>
          <div>Start in the next
            <input
              aria-label="startGracePeriod"
              className={inputClasses}
              min="0"
              placeholder="0"
              type="number"
              value={startGracePeriod}
              onChange={e => setStartGracePeriod(e.target.value)}
            />
            days
          </div>
          <div>Ended in the last
            <input
              aria-label="endGracePeriod"
              className={inputClasses}
              min="0"
              placeholder="0"
              type="number"
              value={endGracePeriod}
              onChange={e => setEndGracePeriod(e.target.value)}
            />
            days
          </div>
        </div>
        <hr />
        <div>
          <strong>Included Academic Terms</strong>
          {!isLoading && terms && termsAndRanges()}
        </div>
      </div>
      <Footer
        primaryButtonCallback={() => saveSettings()}
        // Until the current values load, the inputs still read 0 — saving would zero out a real setting.
        primaryButtonDisabled={!settings}
        primaryButtonSubmit={false}
        primaryButtonText="Save"
        secondaryButtonCallback={() => {
          modalManager.close();
        }}
        submitting={isSaving}
      >
        {isError && (
          <span className="tw-text-red tw-mr-2">{I18n.t('academic_terms_save_error')}</span>
        )}
      </Footer>
    </Modal>
  );
};

const AcademicTermsModalWrapper = ({ modalManager, subscriberId }: AcademicTermsModalProps) => (
  <QueryClientProvider client={new QueryClient()}>
    <AcademicTermsModal modalManager={modalManager} subscriberId={subscriberId} />
  </QueryClientProvider>
);

export default AcademicTermsModalWrapper;
